import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline'
import { api } from '../services/api.ts'
import LoadingSpinner from '../components/LoadingSpinner.tsx'

export default function Settings() {
  const queryClient = useQueryClient()
  const [googleApiKey, setGoogleApiKey] = useState('')
  const [newExcludedName, setNewExcludedName] = useState('')
  const [newKeyword, setNewKeyword] = useState('')
  const [sortExcludedAlphabetically, setSortExcludedAlphabetically] =
    useState(true)

  // Fetch current config
  const { data: config, isLoading: configLoading } = useQuery('config', () =>
    api.get('/api/settings/config/defaults')
  )

  // Fetch API key status
  const { data: apiKeyStatus } = useQuery('api-key-status', () =>
    api.get('/api/settings/config/api-keys/status')
  )

  // Update API keys mutation
  const updateApiKeysMutation = useMutation(
    (data: { google_places_key?: string }) =>
      api.post('/api/settings/config/api-keys', data),
    {
      onSuccess: () => {
        toast.success('API key updated successfully')
        queryClient.invalidateQueries('api-key-status')
        setGoogleApiKey('')
      },
      onError: (error: any) => {
        let errorMessage = 'Failed to update API key'

        if (error.response?.data?.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail
          } else if (Array.isArray(error.response.data.detail)) {
            // Pydantic validation errors
            errorMessage = error.response.data.detail
              .map((err: any) => `${err.loc?.join('.') || 'Field'}: ${err.msg}`)
              .join(', ')
          } else if (typeof error.response.data.detail === 'object') {
            errorMessage = JSON.stringify(error.response.data.detail)
          }
        }

        toast.error(errorMessage)
      },
    }
  )

  // Update excluded names mutation
  const updateExcludedNamesMutation = useMutation(
    (excludedNames: string[]) =>
      api.post('/api/settings/config/excluded-names', {
        excluded_names: excludedNames,
      }),
    {
      onSuccess: () => {
        toast.success('Excluded names updated')
        queryClient.invalidateQueries('config')
      },
      onError: (error: any) => {
        let errorMessage = 'Failed to update excluded names'

        if (error.response?.data?.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail
          } else if (Array.isArray(error.response.data.detail)) {
            // Pydantic validation errors
            errorMessage = error.response.data.detail
              .map((err: any) => `${err.loc?.join('.') || 'Field'}: ${err.msg}`)
              .join(', ')
          } else if (typeof error.response.data.detail === 'object') {
            errorMessage = JSON.stringify(error.response.data.detail)
          }
        }

        toast.error(errorMessage)
      },
    }
  )

  // Update keywords mutation
  const updateKeywordsMutation = useMutation(
    (keywords: string[]) =>
      api.post('/api/settings/config/keywords', { keywords }),
    {
      onSuccess: () => {
        toast.success('Default keywords updated')
        queryClient.invalidateQueries('config')
      },
      onError: (error: any) => {
        let errorMessage = 'Failed to update keywords'

        if (error.response?.data?.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail
          } else if (Array.isArray(error.response.data.detail)) {
            // Pydantic validation errors
            errorMessage = error.response.data.detail
              .map((err: any) => `${err.loc?.join('.') || 'Field'}: ${err.msg}`)
              .join(', ')
          } else if (typeof error.response.data.detail === 'object') {
            errorMessage = JSON.stringify(error.response.data.detail)
          }
        }

        toast.error(errorMessage)
      },
    }
  )

  const handleSaveApiKey = (e: React.FormEvent) => {
    e.preventDefault()
    if (!googleApiKey.trim()) {
      toast.error('Please enter an API key')
      return
    }
    updateApiKeysMutation.mutate({ google_places_key: googleApiKey })
  }

  const handleAddExcludedName = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newExcludedName.trim()) {
      toast.error('Please enter a name')
      return
    }

    const currentExcluded = config?.data?.excluded_names || []
    if (currentExcluded.includes(newExcludedName.trim())) {
      toast.error('This name is already in the excluded list')
      return
    }

    const updatedList = [...currentExcluded, newExcludedName.trim()]
    updateExcludedNamesMutation.mutate(updatedList)
    setNewExcludedName('')
  }

  const handleDeleteExcludedName = (nameToDelete: string) => {
    const currentExcluded = config?.data?.excluded_names || []
    const updatedList = currentExcluded.filter(
      (name: string) => name !== nameToDelete
    )
    updateExcludedNamesMutation.mutate(updatedList)
  }

  const handleAddKeyword = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newKeyword.trim()) {
      toast.error('Please enter a keyword')
      return
    }

    const currentKeywords = config?.data?.default_keywords || []
    if (currentKeywords.includes(newKeyword.trim())) {
      toast.error('This keyword is already in the list')
      return
    }

    const updatedList = [...currentKeywords, newKeyword.trim()]
    updateKeywordsMutation.mutate(updatedList)
    setNewKeyword('')
  }

  const handleDeleteKeyword = (keywordToDelete: string) => {
    const currentKeywords = config?.data?.default_keywords || []
    const updatedList = currentKeywords.filter(
      (keyword: string) => keyword !== keywordToDelete
    )
    updateKeywordsMutation.mutate(updatedList)
  }

  if (configLoading) {
    return <LoadingSpinner />
  }

  const excludedNames = config?.data?.excluded_names || []
  const sortedExcludedNames = sortExcludedAlphabetically
    ? [...excludedNames].sort((a: string, b: string) => a.localeCompare(b))
    : excludedNames

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div>
        <h1 className='text-2xl font-bold text-gray-900 dark:text-white'>
          Settings
        </h1>
        <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
          Configure API keys, keywords, and application settings
        </p>
      </div>

      {/* API Keys Section */}
      <div className='card'>
        <div className='card-header'>
          <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
            API Keys
          </h3>
          <p className='mt-1 text-sm text-gray-500'>
            Manage your Google Places API key
          </p>
        </div>
        <div className='card-body'>
          {/* API Key Status */}
          <div className='mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg'>
            <h4 className='text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              Current Status
            </h4>
            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-gray-600 dark:text-gray-400'>
                  Google Places API
                </span>
                {apiKeyStatus?.data?.google_places_configured ? (
                  <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'>
                    ✓ Configured
                  </span>
                ) : (
                  <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800'>
                    ✗ Not Configured
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Update API Key Form */}
          <form onSubmit={handleSaveApiKey} className='space-y-4'>
            <div>
              <label
                htmlFor='google-api-key'
                className='block text-sm font-medium text-gray-700 dark:text-gray-300'
              >
                Google Places API Key
              </label>
              <div className='mt-1'>
                <input
                  type='text'
                  id='google-api-key'
                  value={googleApiKey}
                  onChange={(e) => setGoogleApiKey(e.target.value)}
                  className='input'
                  placeholder='Enter your Google Places API key'
                />
              </div>
              <p className='mt-2 text-sm text-gray-500'>
                This key is used to search for businesses using Google Places
                API. Get your key from{' '}
                <a
                  href='https://console.cloud.google.com/apis/credentials'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-blue-600 hover:text-blue-500'
                >
                  Google Cloud Console
                </a>
              </p>
            </div>

            <div className='flex justify-end'>
              <button
                type='submit'
                disabled={updateApiKeysMutation.isLoading}
                className='btn-primary'
              >
                {updateApiKeysMutation.isLoading ? 'Saving...' : 'Save API Key'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Excluded Names Section */}
      <div className='card'>
        <div className='card-header'>
          <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
            Excluded Business Names
          </h3>
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Businesses with these names will be filtered out from scraping
            results
          </p>
        </div>
        <div className='card-body'>
          {/* Add New Excluded Name Form */}
          <form onSubmit={handleAddExcludedName} className='mb-6'>
            <div className='flex space-x-2'>
              <input
                type='text'
                value={newExcludedName}
                onChange={(e) => setNewExcludedName(e.target.value)}
                className='input flex-1'
                placeholder='Enter business name to exclude (e.g., Walmart, CVS)'
              />
              <button
                type='submit'
                disabled={updateExcludedNamesMutation.isLoading}
                className='btn-primary inline-flex items-center'
              >
                <PlusIcon className='h-5 w-5 mr-1' />
                Add
              </button>
            </div>
          </form>

          {/* Excluded Names List */}
          <div>
            <div className='flex items-center justify-between mb-3'>
              <h4 className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                Current Excluded Names ({excludedNames.length})
              </h4>
              <button
                onClick={() =>
                  setSortExcludedAlphabetically(!sortExcludedAlphabetically)
                }
                className='text-sm text-blue-600 hover:text-blue-800 font-medium'
              >
                {sortExcludedAlphabetically
                  ? '🔤 A-Z (Click for original order)'
                  : '📋 Original (Click for A-Z)'}
              </button>
            </div>
            {excludedNames.length === 0 ? (
              <div className='text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg'>
                <p className='text-sm text-gray-500 dark:text-gray-400'>
                  No excluded names configured
                </p>
                <p className='text-xs text-gray-400 dark:text-gray-500 mt-1'>
                  Add names above to filter them from scraping results
                </p>
              </div>
            ) : (
              <div className='space-y-2'>
                {sortedExcludedNames.map((name: string) => (
                  <div
                    key={name}
                    className='flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'
                  >
                    <span className='text-sm font-medium text-gray-900 dark:text-white'>
                      {name}
                    </span>
                    <button
                      onClick={() => handleDeleteExcludedName(name)}
                      disabled={updateExcludedNamesMutation.isLoading}
                      className='text-red-600 hover:text-red-700 disabled:opacity-50'
                      title='Remove from excluded list'
                    >
                      <TrashIcon className='h-5 w-5' />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className='mt-6 p-4 bg-blue-50 rounded-lg'>
            <p className='text-sm text-blue-800'>
              <strong>Note:</strong> Excluded names are case-insensitive and
              will match partial names. For example, excluding "Walmart" will
              filter out "Walmart Supercenter", "Walmart Neighborhood Market",
              etc.
            </p>
          </div>
        </div>
      </div>

      {/* Default Keywords Section */}
      <div className='card'>
        <div className='card-header'>
          <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
            Default Keywords
          </h3>
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            These keywords are pre-populated when creating a new job
          </p>
        </div>
        <div className='card-body'>
          {/* Add New Keyword Form */}
          <form onSubmit={handleAddKeyword} className='mb-6'>
            <div className='flex space-x-2 '>
              <input
                type='text'
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                className='input flex-1 dark:bg-gray-200'
                placeholder='Enter keyword (e.g., vape shop, smoke shop)'
              />
              <button
                type='submit'
                disabled={updateKeywordsMutation.isLoading}
                className='btn-primary inline-flex items-center'
              >
                <PlusIcon className='h-5 w-5 mr-1' />
                Add
              </button>
            </div>
          </form>

          {/* Keywords List */}
          <div>
            <h4 className='text-sm font-medium text-gray-700 dark:text-gray-300 mb-3'>
              Current Default Keywords (
              {(config?.data?.default_keywords || []).length})
            </h4>
            {(config?.data?.default_keywords || []).length === 0 ? (
              <div className='text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg'>
                <p className='text-sm text-gray-500 dark:text-gray-400'>
                  No default keywords configured
                </p>
                <p className='text-xs text-gray-400 dark:text-gray-500 mt-1'>
                  Add keywords above to pre-populate new jobs
                </p>
              </div>
            ) : (
              <div className='flex flex-wrap gap-2'>
                {(config?.data?.default_keywords || []).map(
                  (keyword: string) => (
                    <span
                      key={keyword}
                      className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors group'
                    >
                      {keyword}
                      <button
                        onClick={() => handleDeleteKeyword(keyword)}
                        disabled={updateKeywordsMutation.isLoading}
                        className='ml-2 text-blue-600 hover:text-blue-800 disabled:opacity-50'
                        title='Remove keyword'
                      >
                        <TrashIcon className='h-4 w-4' />
                      </button>
                    </span>
                  )
                )}
              </div>
            )}
          </div>

          <p className='mt-4 text-sm text-gray-500 dark:text-gray-300'>
            💡 These keywords will be pre-filled when creating a new job, but
            can be customized for each job
          </p>
        </div>
      </div>
    </div>
  )
}


